/** The overlays that belong to the PRODUCT, not to a layout.
 *
 * These were built inside `AppShell`, which renders on desktop only. That was
 * fine for a rail toggle and wrong for everything else here: the handwritten
 * note chooser shipped mounted only on desktop, so on a phone or a tablet the
 * add-sheet's "New handwritten note…" raised a create intent that nothing was
 * listening for, and the button did nothing at all. The same trap the dialog
 * was moved out of GraphTab to escape — moved, not removed, because the new
 * owner was also conditional.
 *
 * So the rule is now structural rather than remembered: an overlay that any
 * layout can summon is mounted ONCE, here, above the compact/desktop branch.
 * There is no layout in which one of these is absent.
 *
 * The tool context is assembled here for the same reason. The Tools overlay is
 * the product's whole action surface and a touch device needs it more than a
 * desktop does, not less — it has no keyboard to open it and no rails to hide
 * things in.
 */
import { useCallback, useMemo, useRef, type RefObject } from "react";

import { ShortcutSheet } from "@/components/appshell/ShortcutSheet";
import { ToolsOverlay } from "@/components/appshell/ToolsOverlay";
import { BlankNoteDialog } from "@/components/graph/BlankNoteDialog";
import { useUpload } from "@/hooks/use-upload";
import { uploadAccept } from "@/lib/upload-accept";
import { useAnnotationTool } from "@/stores/annotation-tool";
import { useShell } from "@/stores/shell";
import { useTabs } from "@/stores/tabs";
import { useToolsOverlay } from "@/stores/tools-overlay";
import type { ToolContext } from "@/features/tool-registry";

export interface GlobalOverlayActions {
  openPalette: () => void;
  addWebsite: () => void;
  smartExport: () => void;
  manageTokens: () => void;
  manageBackups: () => void;
}

/** The file picker every "Upload" entry point reaches, wherever it lives. */
function useHiddenUpload(): [RefObject<HTMLInputElement | null>, () => void, JSX.Element] {
  const upload = useUpload();
  const ref = useRef<HTMLInputElement | null>(null);
  const pick = useCallback(() => ref.current?.click(), []);
  const input = (
    <input
      ref={ref}
      type="file"
      multiple
      accept={uploadAccept()}
      className="hidden"
      onChange={(e) => {
        if (e.target.files) void upload(e.target.files);
        e.target.value = "";
      }}
    />
  );
  return [ref, pick, input];
}

export function GlobalOverlays({ actions }: { actions: GlobalOverlayActions }) {
  const activeTab = useTabs((s) => s.tabs.find((t) => t.id === s.activeId));
  const activeInkTool = useAnnotationTool((s) => s.tool);
  const pendingCreate = useShell((s) => s.create);
  const closeTools = useToolsOverlay((s) => s.closeTools);
  const [, pickUpload, uploadInput] = useHiddenUpload();

  const { openPalette, addWebsite, smartExport, manageTokens, manageBackups } = actions;

  const toolCtx = useMemo<ToolContext>(
    () => ({
      doc:
        activeTab?.docId && (activeTab.kind === "reader" || activeTab.kind === "markdown")
          ? { id: activeTab.docId, title: activeTab.title, kind: activeTab.kind }
          : null,
      canInk: activeTab?.kind === "reader",
      activeInkTool,
      actions: {
        openPalette,
        addWebsite,
        smartExport,
        manageTokens,
        manageBackups,
        pickUpload,
        closeOverlay: closeTools,
      },
    }),
    [
      activeTab,
      activeInkTool,
      openPalette,
      addWebsite,
      smartExport,
      manageTokens,
      manageBackups,
      pickUpload,
      closeTools,
    ],
  );

  return (
    <>
      <ToolsOverlay ctx={toolCtx} />
      <ShortcutSheet />
      <BlankNoteDialog
        open={pendingCreate === "note"}
        onOpenChange={(next) => {
          if (!next) useShell.getState().clearCreate();
        }}
      />
      {uploadInput}
    </>
  );
}
